import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';
import { useKidsFlasher } from '../context/KidsFlasherContext';
import ErrorMessage from '../components/ErrorMessage';

const POLL_INTERVAL = 5000; // 5 seconds between polls

const LoadingPage = () => {
    const { playerData, error, queueStatus, fetchPlayerData, pollQueueStatus } = useKidsFlasher();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [dots, setDots] = useState('');
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [progress, setProgress] = useState(null);
    const hasFetchedRef = useRef(false);
    const pollTimeoutRef = useRef(null);
    const pollingInProgressRef = useRef(false);

    // Cleanup function
    const cleanup = () => {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
        pollingInProgressRef.current = false;
    };

    // Initial data fetch when component mounts
    useEffect(() => {
        const fetchData = async () => {
            const riotId = searchParams.get('riotId');
            if (riotId && !hasFetchedRef.current) {
                hasFetchedRef.current = true;
                const result = await fetchPlayerData(decodeURIComponent(riotId));
                // Handle instant completion from cache
                if (result.status === 'completed' && result.data) {
                    navigate(`/results/${encodeURIComponent(riotId)}`);
                }
            } else if (!riotId) {
                navigate('/');
            }
        };
        
        fetchData();
        return cleanup;
    }, [searchParams, fetchPlayerData, navigate]);

    // Animation dots effect
    useEffect(() => {
        const interval = setInterval(() => {
            setDots(prev => prev.length >= 3 ? '' : prev + '.');
        }, 500);

        return () => clearInterval(interval);
    }, []);

    // Time elapsed counter
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeElapsed(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Combined polling effect for queue status and progress
    useEffect(() => {
        const startPolling = async () => {
            if (!queueStatus?.requestId || pollingInProgressRef.current) return;

            const poll = async () => {
                if (pollingInProgressRef.current) return;
                pollingInProgressRef.current = true;

                try {
                    console.log('Polling queue status...');
                    const result = await pollQueueStatus(queueStatus.requestId);
                    console.log('Poll result:', result);
                    
                    // Update progress from the combined response
                    if (result?.progress) {
                        setProgress(result.progress);
                    }
                    
                    pollingInProgressRef.current = false;
                    
                    if (result === 'expired') {
                        cleanup();
                        navigate('/');
                        return;
                    }
                    
                    if (result === true) {
                        cleanup();
                        const riotId = searchParams.get('riotId');
                        navigate(`/results/${encodeURIComponent(riotId)}`);
                        return;
                    }
                    
                    // Schedule next poll only if we haven't completed or errored
                    if (!result) {
                        pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    pollingInProgressRef.current = false;
                    pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
                }
            };

            // Start first poll after initial delay
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
        };

        if (queueStatus?.requestId) {
            startPolling();
        }

        return cleanup;
    }, [queueStatus, pollQueueStatus, navigate, searchParams]);

    const getQueueMessage = () => {
        if (!queueStatus) return 'Processing your request' + dots;
        
        const position = queueStatus.position;
        if (position === -1) return 'Request expired. Please try again.';
        if (position === -2 || position === 0) {
            if (progress?.status === 'progress') {
                return `Analyzing matches (${progress.processed}/${progress.total})`;
            }
            return 'Processing your request' + dots;
        }
        return `Queue position: #${position + 1}`;
    };

    const getEstimatedTime = () => {
        if (!queueStatus || queueStatus.position < 0) return '';
        if (queueStatus.position === 0) return '';
        const estimatedMinutes = Math.ceil((queueStatus.position + 1) * 2);
        return `Estimated wait time: ${estimatedMinutes} minute${estimatedMinutes !== 1 ? 's' : ''}`;
    };

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
            <Logo className="w-32 h-32 mb-8" />
            <div className="text-center w-full max-w-md mx-auto">
                <div className="text-2xl text-blue-400 mb-4">
                    {getQueueMessage()}
                </div>
                {progress?.status === 'progress' && (
                    <div className="w-full max-w-sm mx-auto bg-gray-700 rounded-full h-2 mb-4">
                        <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                            style={{ 
                                width: `${(progress.processed / progress.total) * 100}%` 
                            }}
                        />
                    </div>
                )}
                {getEstimatedTime() && (
                    <div className="text-gray-400 mb-4">
                        {getEstimatedTime()}
                    </div>
                )}
                <div className="text-gray-500">
                    Time elapsed: {timeElapsed}s
                </div>
            </div>
        </div>
    );
};

export default LoadingPage;